import React, { useRef, useState } from "react";
import { ABtn, AResult, AScore } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";
import { useInput, useLoop, Joystick, Token, dist, clamp } from "./arena";

// Kho-Kho: weave THROUGH the line of seated kids (mind the gaps!) to cross the
// field and back. The chaser can't pass through the seated row, so slip past it.
const SEATS = [16, 33, 50, 67, 84].map((x, i) => ({ id: i, x, y: 50 }));
const TARGET = 6;   // successful crossings to win
const TIME = 45;
const SEAT_R = 7;   // block radius around a seated kid

export default function KhoKhoRun() {
  const [phase, setPhase] = useState("ready");
  const [crosses, setCrosses] = useState(0);
  const [lives, setLives] = useState(3);
  const [time, setTime] = useState(TIME);
  const [, setTick] = useState(0);
  const player = useRef({ x: 50, y: 88 });
  const chaser = useRef({ x: 50, y: 12 });
  const side = useRef("bottom");
  const grace = useRef(0);
  const { dir, keys, setJoy } = useInput();

  const respawn = () => { player.current = { x: 50, y: 88 }; chaser.current = { x: 50, y: 12 }; side.current = "bottom"; grace.current = 1.1; };
  const start = () => { respawn(); setCrosses(0); setLives(3); setTime(TIME); setPhase("play"); };

  // push a point out of any seated kid it overlaps
  const avoidSeats = (p) => {
    SEATS.forEach((s) => {
      const dx = p.x - s.x, dy = p.y - s.y, d = Math.hypot(dx, dy);
      if (d < SEAT_R && d > 0.001) { p.x = s.x + (dx / d) * SEAT_R; p.y = s.y + (dy / d) * SEAT_R; }
    });
  };

  useLoop(phase === "play", (dt) => {
    grace.current = Math.max(0, grace.current - dt);
    const p = player.current, c = chaser.current, d = dir();
    p.x = clamp(p.x + d.x * 36 * dt, 4, 96);
    p.y = clamp(p.y + d.y * 36 * dt, 4, 96);
    avoidSeats(p);
    // chaser homes in but is blocked by the seated row -> must find a gap
    const ax = p.x - c.x, ay = p.y - c.y, m = Math.hypot(ax, ay) || 1;
    c.x = clamp(c.x + (ax / m) * 27 * dt, 4, 96);
    c.y = clamp(c.y + (ay / m) * 27 * dt, 4, 96);
    avoidSeats(c);
    // crossing the centre line counts
    const nowSide = p.y < 50 ? "top" : "bottom";
    if (nowSide !== side.current) {
      side.current = nowSide;
      setCrosses((x) => { const nx = x + 1; if (nx >= TARGET) { setPhase("win"); sfx.win?.(); } return nx; });
      sfx.hit?.();
    }
    // tagged?
    if (grace.current <= 0 && dist(p, c) < 5) {
      setLives((l) => { const nl = l - 1; if (nl <= 0) setPhase("lose"); return nl; });
      respawn();
    }
    setTime((t) => { const nt = t - dt; if (nt <= 0) { setPhase("lose"); return 0; } return nt; });
    setTick((x) => (x + 1) % 1e6);
    void keys;
  });

  return (
    <div className="w-full text-center">
      <div className="flex flex-wrap justify-center gap-2">
        <AScore label="Time" value={`${Math.ceil(time)}s`} />
        <AScore label="Crossings" value={`${crosses}/${TARGET}`} />
        <AScore label="Lives" value={"❤".repeat(Math.max(0, lives)) || "—"} />
      </div>
      <p className="my-2 text-sm text-kirana-brown/70">Weave between the seated kids to cross the line & back. The chaser can't cut through the row — use the gaps!</p>

      <div data-testid="kk-arena" className="relative mx-auto mb-3 h-72 w-full max-w-md overflow-hidden rounded-md border-2 border-kirana-border bg-gradient-to-b from-[#f4d9a6] to-[#e3ba79]">
        {/* centre line the seated kids sit on */}
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 bg-white/80" />
        {SEATS.map((s) => <Token key={s.id} x={s.x} y={s.y} color="#6d4a2f" label="🧎" size={26} z={4} />)}
        {phase !== "ready" && <Token testid="kk-runner" x={player.current.x} y={player.current.y} color="#1D6FB8" label="😀" size={30} z={8} ring={grace.current > 0} />}
        {phase !== "ready" && <Token testid="kk-chaser" x={chaser.current.x} y={chaser.current.y} color="#E63946" label="🏃" size={30} z={7} />}
      </div>

      {phase === "play" && (
        <div className="flex items-center justify-center gap-4">
          <Joystick onMove={setJoy} />
          <span className="hidden text-xs text-kirana-brown/60 sm:inline">Move: WASD / Arrow keys</span>
        </div>
      )}

      <div className="mt-3">
        {phase === "ready" && <ABtn data-testid="kk-start" onClick={start} tone="gold" className="px-8 py-3">Start Kho-Kho</ABtn>}
        {(phase === "win" || phase === "lose") && (
          <>
            <AResult testId="kk-result" win={phase === "win"} text={phase === "win" ? `Kho-Kho jeet! ${TARGET} crossings! 🏃` : `Out! ${crosses}/${TARGET} crossings.`} />
            <div className="mt-3 flex justify-center"><ABtn data-testid="kk-again" onClick={start} tone="green">Play Again</ABtn></div>
          </>
        )}
      </div>
    </div>
  );
}
